import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { AsyncSection } from "../AgentAnalytics/ds";
import { useTableStyles } from "../AgentAnalytics/tableStyles";
import { fetchAnalytics } from "./adTemplateApi";
import { freeMeter, int, relativeDay } from "./formatters";
import { toLeadRow } from "./publicLeadRow";
import ProjectDrawer from "./ProjectDrawer";

export const PUBLIC_SIGNUP_COLUMNS = [
  { key: "name", label: "Lead" },
  { key: "phone", label: "Phone" },
  { key: "signedUp", label: "Project" },
  { key: "claimedAt", label: "Signed up" },
  { key: "daysToSignup", label: "Days to sign up" },
  { key: "generationsUsed", label: "Public generations" },
];

export const PUBLIC_SIGNUP_EXPORT_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "signedUp", label: "Project" },
  { key: "assistantId", label: "Project ID" },
  { key: "claimedAt", label: "Signed up" },
  { key: "daysToSignup", label: "Days to sign up" },
  { key: "generationsUsed", label: "Public generations" },
];

const DAY_MS = 86400000;

const useLocalStyles = makeStyles({
  centeredTable: {
    "& td, & th": { textAlign: "center" },
  },
  num: {
    textAlign: "center",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
});

const daysBetween = (from, to) => {
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return Math.max(0, Math.floor((end - start) / DAY_MS));
};

const toRow = (lead) => ({
  ...toLeadRow(lead),
  daysToSignup: daysBetween(lead.createdAt, lead.claimedAt),
});

export default function PublicSignupsView({ range, search, refreshKey, onRows }) {
  const classes = { ...useTableStyles(), ...useLocalStyles() };
  const [state, setState] = useState({
    loading: true,
    notFound: false,
    items: [],
    total: 0,
    error: null,
  });
  const [page, setPage] = useState(0);
  const [project, setProject] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [range.from, range.to, search]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fetchAnalytics("public-leads", {
      from: range.from,
      to: range.to,
      page: page + 1,
      search,
      stage: "signed_up",
      sort: "claimedAt",
      dir: "desc",
    }).then((r) => {
      if (!alive) return;
      const items = ((r.data && r.data.items) || []).map(toRow);
      setState({
        loading: false,
        notFound: r.notFound,
        items,
        total: (r.data && r.data.total) || 0,
        error: r.error,
      });
      if (onRows) onRows(items, PUBLIC_SIGNUP_EXPORT_COLUMNS);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, page, search, refreshKey]);

  const openProject = (lead) =>
    lead.assistantId &&
    setProject({
      assistantId: lead.assistantId,
      projectName: lead.projectName,
      clientId: lead.clientId,
      currency: "INR",
    });

  return (
    <>
      <AsyncSection
        loading={state.loading}
        notFound={state.notFound}
        error={state.error}
        empty={!state.items.length}
        emptyTitle="No signups from the public page in this range"
        emptyDescription="Leads who later create an account show up here with their project."
      >
        <Box data-testid="public-signups-table">
          <div className={`${classes.tableWrap} ${classes.centeredTable}`}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {PUBLIC_SIGNUP_COLUMNS.map((c) => (
                    <TableCell key={c.key} align="center" className={classes.headCell}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.items.map((lead) => (
                  <TableRow
                    key={lead._id}
                    hover
                    className={classes.clickRow}
                    data-testid={`signup-row-${lead._id}`}
                    onClick={() => openProject(lead)}
                  >
                    <TableCell className={classes.cell}>
                      <Typography component="div" className={classes.nameText}>
                        {lead.name || "—"}
                      </Typography>
                      <Typography component="div" className={classes.monoSub}>
                        {lead.email || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {lead.phone}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Typography component="div" className={classes.nameText}>
                        {lead.projectName || lead.assistantId || "—"}
                      </Typography>
                      {lead.projectName ? (
                        <Typography component="div" className={classes.monoSub}>
                          {lead.assistantId}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell className={classes.cell}>{relativeDay(lead.claimedAt)}</TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {int(lead.daysToSignup)}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {freeMeter(lead.generationsUsed || 0, lead.generationsLimit)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <TablePagination
            component="div"
            count={state.total}
            page={page}
            onChangePage={(event, next) => setPage(next)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        </Box>
      </AsyncSection>

      <ProjectDrawer project={project} range={range} onClose={() => setProject(null)} />
    </>
  );
}
